import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Users,
  Plus,
  Trophy,
  MessageCircle,
  Target,
  Crown,
  Shield,
  User,
  Send,
  ChevronRight,
  ChevronLeft,
  Settings,
  Eye,
  EyeOff,
  BarChart3,
  TrendingUp,
  UserPlus,
  Check,
  X,
  Clock,
  Award,
  Dumbbell,
  Calendar
} from 'lucide-react';

export default function TeamsPage() {
  const { token, user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [myInvites, setMyInvites] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMessages, setTeamMessages] = useState([]);
  const [teamChallenges, setTeamChallenges] = useState([]);
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Create team state
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  
  // Create challenge state
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [newChallenge, setNewChallenge] = useState({
    title: '',
    description: '',
    challenge_type: 'points',
    target_value: 100,
    end_date: ''
  });
  
  // Invite state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [inviting, setInviting] = useState(false);
  
  // Privacy settings state
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    show_name: true,
    show_stats: true,
    show_progress: true
  });

  // Team workouts state
  const [teamWorkouts, setTeamWorkouts] = useState([]);
  const [showAssignWorkout, setShowAssignWorkout] = useState(false);
  const [availableWorkouts, setAvailableWorkouts] = useState([]);
  const [selectedWorkoutId, setSelectedWorkoutId] = useState('');
  const [workoutDate, setWorkoutDate] = useState('');
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [assigningWorkout, setAssigningWorkout] = useState(false);
  const [workoutLeaderboard, setWorkoutLeaderboard] = useState([]);
  const [selectedWorkoutCompletions, setSelectedWorkoutCompletions] = useState(null);
  const [teamBadges, setTeamBadges] = useState([]);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [showBadgesModal, setShowBadgesModal] = useState(false);

  const isTrainer = user?.role === 'trainer' || user?.role === 'admin';

  const fetchTeams = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  }, [token]);

  const fetchInvites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/teams/invites/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyInvites(response.data);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  }, [token]);

  const fetchTeamDetails = useCallback(async (teamId) => {
    try {
      const [membersRes, messagesRes, challengesRes, leaderboardRes, workoutsRes, workoutLeaderboardRes, badgesRes] = await Promise.all([
        axios.get(`${API}/teams/${teamId}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/teams/${teamId}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/teams/${teamId}/challenges`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/teams/${teamId}/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/teams/${teamId}/workouts`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/teams/${teamId}/workouts/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/teams/${teamId}/badges`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTeamMembers(membersRes.data);
      setTeamMessages(messagesRes.data);
      setTeamChallenges(challengesRes.data);
      setTeamLeaderboard(leaderboardRes.data);
      setTeamWorkouts(workoutsRes.data);
      setWorkoutLeaderboard(workoutLeaderboardRes.data);
      setTeamBadges(badgesRes.data);
      
      // Get current user's privacy settings
      const currentMember = membersRes.data.find(m => m.user_id === user?.id);
      if (currentMember?.privacy_settings) {
        setPrivacySettings(currentMember.privacy_settings);
      }
    } catch (error) {
      console.error('Error fetching team details:', error);
    }
  }, [token, user?.id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTeams(), fetchInvites()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTeams, fetchInvites]);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamDetails(selectedTeam.id);
    }
  }, [selectedTeam, fetchTeamDetails]);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    
    setCreatingTeam(true);
    try {
      await axios.post(
        `${API}/teams?name=${encodeURIComponent(newTeamName)}&description=${encodeURIComponent(newTeamDescription)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Team created successfully!');
      setShowCreateTeam(false);
      setNewTeamName('');
      setNewTeamDescription('');
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleRespondToInvite = async (inviteId, accept) => {
    try {
      await axios.post(
        `${API}/teams/invites/${inviteId}/respond?accept=${accept}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(accept ? 'Joined team!' : 'Invite declined');
      fetchTeams();
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to invite');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTeam) return;
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/teams/${selectedTeam.id}/messages?content=${encodeURIComponent(newMessage)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchTeamDetails(selectedTeam.id);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateChallenge = async () => {
    if (!newChallenge.title.trim() || !selectedTeam) {
      toast.error('Please enter a challenge title');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        title: newChallenge.title,
        challenge_type: newChallenge.challenge_type,
        target_value: newChallenge.target_value.toString()
      });
      if (newChallenge.description) params.append('description', newChallenge.description);
      if (newChallenge.end_date) params.append('end_date', newChallenge.end_date);
      
      await axios.post(
        `${API}/teams/${selectedTeam.id}/challenges?${params}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Challenge created!');
      setShowCreateChallenge(false);
      setNewChallenge({ title: '', description: '', challenge_type: 'points', target_value: 100, end_date: '' });
      fetchTeamDetails(selectedTeam.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create challenge');
    }
  };

  const handleInviteUser = async (userId) => {
    if (!selectedTeam) return;
    
    setInviting(true);
    try {
      await axios.post(
        `${API}/teams/${selectedTeam.id}/invite?user_id=${userId}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invitation sent!');
      setShowInviteModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdatePrivacy = async () => {
    if (!selectedTeam) return;
    
    try {
      const params = new URLSearchParams({
        show_name: privacySettings.show_name.toString(),
        show_stats: privacySettings.show_stats.toString(),
        show_progress: privacySettings.show_progress.toString()
      });
      
      await axios.put(
        `${API}/teams/${selectedTeam.id}/members/${user.id}/privacy?${params}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Privacy settings updated!');
      setShowPrivacySettings(false);
      fetchTeamDetails(selectedTeam.id);
    } catch (error) {
      toast.error('Failed to update privacy settings');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/trainer/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const memberIds = new Set(teamMembers.map(m => m.user_id));
      setAllUsers(response.data.filter(u => !memberIds.has(u.id)));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAvailableWorkouts = async () => {
    try {
      const response = await axios.get(`${API}/workouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableWorkouts(response.data);
    } catch (error) {
      console.error('Error fetching workouts:', error);
    }
  };

  const fetchAvailableBadges = async () => {
    try {
      const response = await axios.get(`${API}/teams/badges/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableBadges(response.data);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const handleAssignWorkout = async () => {
    if (!selectedTeam || !selectedWorkoutId || !workoutDate) {
      toast.error('Please select a workout and date');
      return;
    }
    
    setAssigningWorkout(true);
    try {
      const params = new URLSearchParams({
        workout_id: selectedWorkoutId,
        scheduled_date: workoutDate
      });
      if (workoutNotes) params.append('notes', workoutNotes);
      
      const response = await axios.post(
        `${API}/teams/${selectedTeam.id}/workouts?${params}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Workout assigned to ${response.data.scheduled_for_members} team members!`);
      setShowAssignWorkout(false);
      setSelectedWorkoutId('');
      setWorkoutDate('');
      setWorkoutNotes('');
      fetchTeamDetails(selectedTeam.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign workout');
    } finally {
      setAssigningWorkout(false);
    }
  };

  const handleRemoveTeamWorkout = async (workoutAssignmentId) => {
    if (!selectedTeam) return;
    if (!window.confirm('Remove this workout assignment? Scheduled workouts (not completed) will also be removed from members.')) return;
    
    try {
      await axios.delete(
        `${API}/teams/${selectedTeam.id}/workouts/${workoutAssignmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Team workout removed');
      fetchTeamDetails(selectedTeam.id);
    } catch (error) {
      toast.error('Failed to remove workout');
    }
  };

  const handleViewCompletions = async (workoutAssignmentId) => {
    if (!selectedTeam) return;
    
    try {
      const response = await axios.get(
        `${API}/teams/${selectedTeam.id}/workouts/${workoutAssignmentId}/completions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedWorkoutCompletions(response.data);
    } catch (error) {
      toast.error('Failed to fetch completions');
    }
  };

  const handleLeaveTeam = async () => {
    if (!selectedTeam) return;
    
    if (!window.confirm('Are you sure you want to leave this team?')) return;
    
    try {
      await axios.delete(
        `${API}/teams/${selectedTeam.id}/members/${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Left team');
      setSelectedTeam(null);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave team');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Team detail view
  if (selectedTeam) {
    const userRole = selectedTeam.user_role;
    const canManage = userRole === 'owner' || userRole === 'admin';
    
    return (
      <div data-testid="team-detail-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => setSelectedTeam(null)}
            className="text-gray-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase">
              {selectedTeam.name}
            </h1>
            <p className="text-gray-400 text-sm">{selectedTeam.description || 'No description'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPrivacySettings(true)}
              className="border-gray-600 text-gray-400"
            >
              <Settings className="w-4 h-4 mr-1" />
              Privacy
            </Button>
            {userRole !== 'owner' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLeaveTeam}
                className="text-red-400 hover:text-red-300"
              >
                Leave Team
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-tactical text-center">
            <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{teamMembers.length}</div>
            <div className="text-xs text-gray-400">Members</div>
          </div>
          <div className="card-tactical text-center">
            <MessageCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{teamMessages.length}</div>
            <div className="text-xs text-gray-400">Messages</div>
          </div>
          <div className="card-tactical text-center">
            <Target className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{teamChallenges.filter(c => c.status === 'active').length}</div>
            <div className="text-xs text-gray-400">Active Challenges</div>
          </div>
          <div className="card-tactical text-center">
            <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">
              {teamLeaderboard[0]?.total_points || 0}
            </div>
            <div className="text-xs text-gray-400">Top Score</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
          {['members', 'chat', 'workouts', 'challenges', 'leaderboard'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? 'bg-primary' : 'text-gray-400'}
            >
              {tab === 'members' && <Users className="w-4 h-4 mr-1" />}
              {tab === 'chat' && <MessageCircle className="w-4 h-4 mr-1" />}
              {tab === 'workouts' && <Dumbbell className="w-4 h-4 mr-1" />}
              {tab === 'challenges' && <Target className="w-4 h-4 mr-1" />}
              {tab === 'leaderboard' && <Trophy className="w-4 h-4 mr-1" />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="card-tactical">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg text-white uppercase">Team Members</h2>
              {canManage && (
                <Button
                  size="sm"
                  onClick={() => {
                    fetchAllUsers();
                    setShowInviteModal(true);
                  }}
                  className="bg-primary hover:bg-red-700"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Invite
                </Button>
              )}
            </div>
            
            <div className="space-y-3">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      member.role === 'owner' ? 'bg-yellow-500/20' :
                      member.role === 'admin' ? 'bg-blue-500/20' : 'bg-gray-500/20'
                    }`}>
                      {member.role === 'owner' ? (
                        <Crown className="w-5 h-5 text-yellow-500" />
                      ) : member.role === 'admin' ? (
                        <Shield className="w-5 h-5 text-blue-500" />
                      ) : (
                        <User className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <div className="text-white font-medium">{member.user_name}</div>
                      <div className="text-xs text-gray-400 capitalize">{member.role}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {member.stats && (
                      <div className="text-right">
                        <div className="text-sm text-primary font-mono">{member.stats.total_points} pts</div>
                        <div className="text-xs text-gray-500">{member.stats.current_streak} day streak</div>
                      </div>
                    )}
                    
                    <div className="flex gap-1">
                      {member.privacy_settings?.show_name ? (
                        <Eye className="w-4 h-4 text-gray-500" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-600" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="card-tactical">
            <h2 className="font-heading text-lg text-white uppercase mb-4">Team Chat</h2>
            
            <div className="h-80 overflow-y-auto mb-4 space-y-3 p-3 bg-secondary/20 rounded-sm">
              {teamMessages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start the conversation!</p>
                </div>
              ) : (
                teamMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] p-3 rounded-sm ${
                      msg.sender_id === user?.id
                        ? 'bg-primary/20 border border-primary/30'
                        : 'bg-secondary/50 border border-border'
                    }`}>
                      {msg.sender_id !== user?.id && (
                        <div className="text-xs text-primary font-medium mb-1">{msg.sender_name}</div>
                      )}
                      <div className="text-white text-sm">{msg.content}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="bg-secondary border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <Button
                onClick={handleSendMessage}
                disabled={sendingMessage || !newMessage.trim()}
                className="bg-primary hover:bg-red-700"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Workouts Tab */}
        {activeTab === 'workouts' && (
          <div className="space-y-6">
            {/* Workout Completion Leaderboard */}
            <div className="card-tactical">
              <h2 className="font-heading text-lg text-white uppercase mb-4">
                <Trophy className="w-5 h-5 inline mr-2 text-yellow-500" />
                Workout Completion Leaders
              </h2>
              
              {workoutLeaderboard.length === 0 ? (
                <div className="text-center text-gray-500 py-4">
                  <p className="text-sm">Complete team workouts to appear on the leaderboard!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {workoutLeaderboard.slice(0, 5).map((entry) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center justify-between p-3 rounded-sm border ${
                        entry.rank <= 3
                          ? 'bg-gradient-to-r from-yellow-500/10 to-transparent border-yellow-500/30'
                          : 'bg-secondary/30 border-border'
                      } ${entry.user_id === user?.id ? 'ring-1 ring-primary' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          entry.rank === 1 ? 'bg-yellow-500 text-black' :
                          entry.rank === 2 ? 'bg-gray-300 text-black' :
                          entry.rank === 3 ? 'bg-amber-700 text-white' :
                          'bg-secondary text-gray-400'
                        }`}>
                          {entry.rank <= 3 ? (
                            entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'
                          ) : (
                            `#${entry.rank}`
                          )}
                        </div>
                        <div>
                          <div className="text-white font-medium text-sm">{entry.user_name}</div>
                          <div className="text-xs text-gray-500">
                            {entry.completed}/{entry.total_assigned} completed
                            {entry.on_time_rate > 0 && (
                              <span className="ml-2 text-green-400">• {entry.on_time_rate}% on time</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Streak display */}
                        {entry.current_streak > 0 && (
                          <div className="text-center">
                            <div className="flex items-center gap-1 text-orange-400">
                              <TrendingUp className="w-4 h-4" />
                              <span className="font-mono font-bold">{entry.current_streak}</span>
                            </div>
                            <div className="text-xs text-gray-500">streak</div>
                          </div>
                        )}
                        <div className="text-right">
                          <div className="text-lg font-mono text-primary">{entry.completion_rate}%</div>
                          <div className="text-xs text-gray-500">rate</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Assigned Workouts */}
            <div className="card-tactical">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg text-white uppercase">Assigned Workouts</h2>
                {canManage && (
                  <Button
                    size="sm"
                    onClick={() => {
                      fetchAvailableWorkouts();
                      setShowAssignWorkout(true);
                    }}
                    className="bg-primary hover:bg-red-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Assign Workout
                  </Button>
                )}
              </div>
              
              {teamWorkouts.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <Dumbbell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No workouts assigned yet</p>
                  {canManage && <p className="text-sm">Assign workouts to train your team together!</p>}
                </div>
              ) : (
                <div className="space-y-3">
                  {teamWorkouts.map((tw) => (
                    <div
                      key={tw.id}
                      className="p-4 rounded-sm border bg-primary/10 border-primary/30"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-semibold">{tw.workout_title}</h3>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-sm text-primary">
                            <Calendar className="w-4 h-4" />
                            {tw.scheduled_date}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewCompletions(tw.id)}
                            className="text-blue-400 hover:text-blue-300 h-7 px-2"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          {canManage && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveTeamWorkout(tw.id)}
                              className="text-red-400 hover:text-red-300 h-7 px-2"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {tw.notes && (
                        <p className="text-gray-400 text-sm mb-2">{tw.notes}</p>
                      )}
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>Assigned by {tw.assigned_by_name}</span>
                        {tw.workout && (
                          <span>{tw.workout.exercises?.length || 0} exercises</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Streak Badges */}
            <div className="card-tactical">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg text-white uppercase">
                  <Award className="w-5 h-5 inline mr-2 text-purple-500" />
                  Streak Badges
                </h2>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    fetchAvailableBadges();
                    setShowBadgesModal(true);
                  }}
                  className="text-gray-400"
                >
                  View All Badges
                </Button>
              </div>
              
              {teamBadges.length === 0 ? (
                <div className="text-center text-gray-500 py-6">
                  <Award className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No badges earned yet</p>
                  <p className="text-sm">Complete consecutive team workouts to earn badges!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {teamBadges.slice(0, 5).map((badge, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-sm bg-gradient-to-r from-purple-500/10 to-transparent border border-purple-500/30"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                          {badge.badge_name === 'Rising Star' && '⭐'}
                          {badge.badge_name === '5-Day Warrior' && '🛡️'}
                          {badge.badge_name === 'Week Warrior' && '🏆'}
                          {badge.badge_name === '10-Day Champion' && '👑'}
                          {badge.badge_name === 'Unstoppable' && '🔥'}
                          {badge.badge_name === 'Habit Master' && '🎖️'}
                          {badge.badge_name === 'Month of Excellence' && '💎'}
                        </div>
                        <div>
                          <div className="text-white font-medium">{badge.badge_name}</div>
                          <div className="text-xs text-gray-500">
                            Earned by {badge.user_name} • {badge.streak_achieved} streak
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(badge.awarded_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                  {teamBadges.length > 5 && (
                    <p className="text-center text-sm text-gray-500">
                      +{teamBadges.length - 5} more badges earned
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Workout Completions Modal */}
        {selectedWorkoutCompletions && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-sm border border-border p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl text-white font-semibold">Workout Completions</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedWorkoutCompletions(null)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="mb-4 p-3 bg-secondary/30 rounded-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Completion Rate</span>
                  <span className="text-2xl font-mono text-primary">
                    {selectedWorkoutCompletions.completion_rate}%
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  {selectedWorkoutCompletions.completed} of {selectedWorkoutCompletions.total_members} members completed
                </div>
              </div>
              
              <div className="space-y-2">
                {selectedWorkoutCompletions.members.map((m, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-sm border ${
                      m.status === 'completed'
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-secondary/30 border-border'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {m.status === 'completed' ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <Clock className="w-5 h-5 text-gray-500" />
                      )}
                      <span className="text-white">{m.user_name}</span>
                    </div>
                    <div className="text-right">
                      {m.status === 'completed' ? (
                        <div className="text-green-400 text-sm">
                          {m.completion_hours ? `${m.completion_hours}h` : 'Done'}
                        </div>
                      ) : (
                        <div className="text-gray-500 text-sm">Pending</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <Button
                variant="outline"
                onClick={() => setSelectedWorkoutCompletions(null)}
                className="w-full mt-4"
              >
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Badges Modal */}
        {showBadgesModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-sm border border-border p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl text-white font-semibold">
                  <Award className="w-5 h-5 inline mr-2 text-purple-500" />
                  Streak Badges
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowBadgesModal(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <p className="text-gray-400 text-sm mb-6">
                Complete consecutive team workouts to earn these badges and bonus points!
              </p>
              
              <div className="space-y-3">
                {availableBadges.map((badge, idx) => {
                  const earned = teamBadges.some(tb => tb.badge_id === badge.id && tb.user_id === user?.id);
                  return (
                    <div
                      key={idx}
                      className={`p-4 rounded-sm border ${
                        earned
                          ? 'bg-green-500/10 border-green-500/30'
                          : 'bg-secondary/30 border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                            badge.color === 'bronze' ? 'bg-amber-700/30' :
                            badge.color === 'silver' ? 'bg-gray-400/30' :
                            badge.color === 'gold' ? 'bg-yellow-500/30' :
                            badge.color === 'platinum' ? 'bg-blue-300/30' :
                            'bg-purple-500/30'
                          }`}>
                            {badge.icon === 'star' && '⭐'}
                            {badge.icon === 'shield' && '🛡️'}
                            {badge.icon === 'trophy' && '🏆'}
                            {badge.icon === 'crown' && '👑'}
                            {badge.icon === 'flame' && '🔥'}
                            {badge.icon === 'medal' && '🎖️'}
                            {badge.icon === 'gem' && '💎'}
                          </div>
                          <div>
                            <div className="text-white font-semibold">{badge.name}</div>
                            <div className="text-sm text-gray-400">{badge.description}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-mono ${earned ? 'text-green-400' : 'text-gray-500'}`}>
                            {badge.streak_required}
                          </div>
                          <div className="text-xs text-gray-500">streak</div>
                          <div className="text-xs text-primary mt-1">+{badge.points} pts</div>
                        </div>
                      </div>
                      {earned && (
                        <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          You've earned this badge!
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                onClick={() => setShowBadgesModal(false)}
                className="w-full mt-6"
              >
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Challenges Tab */}
        {activeTab === 'challenges' && (
          <div className="card-tactical">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg text-white uppercase">Team Challenges</h2>
              {canManage && (
                <Button
                  size="sm"
                  onClick={() => setShowCreateChallenge(true)}
                  className="bg-primary hover:bg-red-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Challenge
                </Button>
              )}
            </div>
            
            {teamChallenges.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No challenges yet</p>
                {canManage && <p className="text-sm">Create one to motivate your team!</p>}
              </div>
            ) : (
              <div className="space-y-3">
                {teamChallenges.map((challenge) => (
                  <div
                    key={challenge.id}
                    className={`p-4 rounded-sm border ${
                      challenge.status === 'active'
                        ? 'bg-purple-500/10 border-purple-500/30'
                        : 'bg-secondary/30 border-border'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-white font-semibold">{challenge.title}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        challenge.status === 'active'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {challenge.status}
                      </span>
                    </div>
                    {challenge.description && (
                      <p className="text-gray-400 text-sm mb-2">{challenge.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Type: {challenge.challenge_type}</span>
                      <span>Target: {challenge.target_value}</span>
                      <span>Ends: {challenge.end_date}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="card-tactical">
            <h2 className="font-heading text-lg text-white uppercase mb-4">Team Leaderboard</h2>
            
            {teamLeaderboard.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <Trophy className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No leaderboard data yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {teamLeaderboard.map((entry) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center justify-between p-3 rounded-sm border ${
                      entry.rank <= 3
                        ? 'bg-gradient-to-r from-yellow-500/10 to-transparent border-yellow-500/30'
                        : 'bg-secondary/30 border-border'
                    } ${entry.user_id === user?.id ? 'ring-1 ring-primary' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        entry.rank === 1 ? 'bg-yellow-500 text-black' :
                        entry.rank === 2 ? 'bg-gray-300 text-black' :
                        entry.rank === 3 ? 'bg-amber-700 text-white' :
                        'bg-secondary text-gray-400'
                      }`}>
                        {entry.rank <= 3 ? (
                          entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'
                        ) : (
                          `#${entry.rank}`
                        )}
                      </div>
                      <div>
                        <div className="text-white font-medium">{entry.user_name}</div>
                        <div className="text-xs text-gray-400">
                          {entry.current_streak} day streak • {entry.badges_count} badges
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-mono text-primary">{entry.total_points}</div>
                      <div className="text-xs text-gray-500">points</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Privacy Settings Modal */}
        {showPrivacySettings && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-sm border border-border p-6 max-w-md w-full">
              <h3 className="text-xl text-white font-semibold mb-4">Privacy Settings</h3>
              <p className="text-gray-400 text-sm mb-6">Control what other team members can see about you</p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white">Show Name</Label>
                    <p className="text-xs text-gray-500">Display your name to team members</p>
                  </div>
                  <Switch
                    checked={privacySettings.show_name}
                    onCheckedChange={(checked) => setPrivacySettings(p => ({ ...p, show_name: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white">Show Stats</Label>
                    <p className="text-xs text-gray-500">Display points, streak, badges</p>
                  </div>
                  <Switch
                    checked={privacySettings.show_stats}
                    onCheckedChange={(checked) => setPrivacySettings(p => ({ ...p, show_stats: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white">Show Progress</Label>
                    <p className="text-xs text-gray-500">Display weight and fitness progress</p>
                  </div>
                  <Switch
                    checked={privacySettings.show_progress}
                    onCheckedChange={(checked) => setPrivacySettings(p => ({ ...p, show_progress: checked }))}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowPrivacySettings(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdatePrivacy}
                  className="flex-1 bg-primary hover:bg-red-700"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Create Challenge Modal */}
        {showCreateChallenge && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-sm border border-border p-6 max-w-md w-full">
              <h3 className="text-xl text-white font-semibold mb-4">Create Challenge</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-white">Title</Label>
                  <Input
                    value={newChallenge.title}
                    onChange={(e) => setNewChallenge(c => ({ ...c, title: e.target.value }))}
                    placeholder="Challenge title"
                    className="bg-secondary border-transparent mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-white">Description (Optional)</Label>
                  <Input
                    value={newChallenge.description}
                    onChange={(e) => setNewChallenge(c => ({ ...c, description: e.target.value }))}
                    placeholder="Challenge description"
                    className="bg-secondary border-transparent mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-white">Type</Label>
                  <select
                    value={newChallenge.challenge_type}
                    onChange={(e) => setNewChallenge(c => ({ ...c, challenge_type: e.target.value }))}
                    className="w-full mt-1 bg-secondary text-white rounded-sm p-2 border-transparent"
                  >
                    <option value="points">Points</option>
                    <option value="workouts">Workouts</option>
                    <option value="steps">Steps</option>
                    <option value="streaks">Streaks</option>
                  </select>
                </div>
                
                <div>
                  <Label className="text-white">Target Value</Label>
                  <Input
                    type="number"
                    value={newChallenge.target_value}
                    onChange={(e) => setNewChallenge(c => ({ ...c, target_value: parseInt(e.target.value) || 0 }))}
                    className="bg-secondary border-transparent mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-white">End Date</Label>
                  <Input
                    type="date"
                    value={newChallenge.end_date}
                    onChange={(e) => setNewChallenge(c => ({ ...c, end_date: e.target.value }))}
                    className="bg-secondary border-transparent mt-1"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateChallenge(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateChallenge}
                  className="flex-1 bg-primary hover:bg-red-700"
                >
                  Create
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-sm border border-border p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl text-white font-semibold mb-4">Invite Members</h3>
              
              {allUsers.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No users available to invite</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {allUsers.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm"
                    >
                      <div>
                        <div className="text-white">{u.first_name} {u.last_name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleInviteUser(u.id)}
                        disabled={inviting}
                        className="bg-primary hover:bg-red-700"
                      >
                        Invite
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                variant="outline"
                onClick={() => setShowInviteModal(false)}
                className="w-full mt-4"
              >
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Assign Workout Modal */}
        {showAssignWorkout && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-sm border border-border p-6 max-w-md w-full">
              <h3 className="text-xl text-white font-semibold mb-4">Assign Workout to Team</h3>
              <p className="text-gray-400 text-sm mb-6">This workout will be scheduled for all team members</p>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-white">Select Workout</Label>
                  <select
                    value={selectedWorkoutId}
                    onChange={(e) => setSelectedWorkoutId(e.target.value)}
                    className="w-full mt-1 bg-secondary text-white rounded-sm p-2 border-transparent"
                  >
                    <option value="">Choose a workout...</option>
                    {availableWorkouts.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.title} ({w.difficulty})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label className="text-white">Scheduled Date</Label>
                  <Input
                    type="date"
                    value={workoutDate}
                    onChange={(e) => setWorkoutDate(e.target.value)}
                    className="bg-secondary border-transparent mt-1"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                
                <div>
                  <Label className="text-white">Notes (Optional)</Label>
                  <Input
                    value={workoutNotes}
                    onChange={(e) => setWorkoutNotes(e.target.value)}
                    placeholder="Any special instructions..."
                    className="bg-secondary border-transparent mt-1"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowAssignWorkout(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignWorkout}
                  disabled={assigningWorkout || !selectedWorkoutId || !workoutDate}
                  className="flex-1 bg-primary hover:bg-red-700"
                >
                  {assigningWorkout ? 'Assigning...' : 'Assign to Team'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Team list view
  return (
    <div data-testid="teams-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            MY <span className="text-primary">TEAMS</span>
          </h1>
          <p className="text-gray-400 mt-1">Collaborate and compete with your fitness groups</p>
        </div>
        {isTrainer && (
          <Button
            onClick={() => setShowCreateTeam(true)}
            className="bg-primary hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Team
          </Button>
        )}
      </div>

      {/* Pending Invites */}
      {myInvites.length > 0 && (
        <div className="card-tactical border-yellow-500/30">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-yellow-500" />
            <h2 className="font-heading text-lg text-white uppercase">Pending Invites</h2>
          </div>
          
          <div className="space-y-3">
            {myInvites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border"
              >
                <div>
                  <div className="text-white font-medium">{invite.team_name}</div>
                  <div className="text-xs text-gray-500">
                    Invited {new Date(invite.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleRespondToInvite(invite.id, true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRespondToInvite(invite.id, false)}
                    className="border-red-500 text-red-400 hover:bg-red-500/20"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Teams List */}
      {teams.length === 0 ? (
        <div className="card-tactical text-center py-12">
          <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No Teams Yet</h3>
          <p className="text-gray-400 mb-4">
            {isTrainer
              ? "Create a team to organize your clients into groups"
              : "You're not part of any teams yet. Wait for an invite from your trainer."}
          </p>
          {isTrainer && (
            <Button
              onClick={() => setShowCreateTeam(true)}
              className="bg-primary hover:bg-red-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Team
            </Button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((team) => (
            <div
              key={team.id}
              onClick={() => setSelectedTeam(team)}
              className="card-tactical cursor-pointer hover:border-primary/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold">{team.name}</h3>
                  <div className="text-xs text-gray-400 capitalize">{team.user_role}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
              
              {team.description && (
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">{team.description}</p>
              )}
              
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{team.member_count} members</span>
                </div>
                <div className="flex items-center gap-1">
                  {team.user_role === 'owner' && <Crown className="w-4 h-4 text-yellow-500" />}
                  {team.user_role === 'admin' && <Shield className="w-4 h-4 text-blue-500" />}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateTeam && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-sm border border-border p-6 max-w-md w-full">
            <h3 className="text-xl text-white font-semibold mb-4">Create New Team</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white">Team Name</Label>
                <Input
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Enter team name"
                  className="bg-secondary border-transparent mt-1"
                />
              </div>
              
              <div>
                <Label className="text-white">Description (Optional)</Label>
                <Input
                  value={newTeamDescription}
                  onChange={(e) => setNewTeamDescription(e.target.value)}
                  placeholder="Team description"
                  className="bg-secondary border-transparent mt-1"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCreateTeam(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTeam}
                disabled={creatingTeam}
                className="flex-1 bg-primary hover:bg-red-700"
              >
                {creatingTeam ? 'Creating...' : 'Create Team'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
